import { useEffect, useState } from 'react';
import type {
  Rule,
  RuleCondition,
  RuleAction,
  RuleConditions,
  RuleConditionField,
  RuleConditionOperator,
  Folder,
} from '@/types/email';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Trash2, Plus, ArrowLeft, Save } from 'lucide-react';

interface RuleFormProps {
  rule?: Rule | null;
  folders: Folder[];
  onSave: (rule: Partial<Rule>) => Promise<void>;
  onCancel: () => void;
}

export function RuleForm({ rule, folders, onSave, onCancel }: RuleFormProps) {
  const [name, setName] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [priority, setPriority] = useState(100);
  const [runOn, setRunOn] = useState<'inbound' | 'outbound' | 'both'>('inbound');
  const [conditionType, setConditionType] = useState<'all' | 'any'>('all');
  const [conditions, setConditions] = useState<RuleCondition[]>([]);
  const [actions, setActions] = useState<RuleAction[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (rule) {
      setName(rule.name);
      setEnabled(rule.enabled);
      setPriority(rule.priority);
      setRunOn(rule.runOn);
      setConditionType(rule.conditions.type);
      setConditions(rule.conditions.conditions);
      setActions(rule.actions);
    } else {
      setName('');
      setEnabled(true);
      setPriority(100);
      setRunOn('inbound');
      setConditionType('all');
      setConditions([]);
      setActions([]);
    }
  }, [rule]);

  const addCondition = () => {
    setConditions([...conditions, { field: 'from', operator: 'contains', value: '' }]);
  };

  const updateCondition = (index: number, updates: Partial<RuleCondition>) => {
    setConditions(conditions.map((c, i) => (i === index ? { ...c, ...updates } : c)));
  };

  const removeCondition = (index: number) => {
    setConditions(conditions.filter((_, i) => i !== index));
  };

  const addAction = () => {
    setActions([...actions, { type: 'markAsRead', value: true }]);
  };

  const updateAction = (index: number, action: RuleAction) => {
    setActions(actions.map((a, i) => (i === index ? action : a)));
  };

  const removeAction = (index: number) => {
    setActions(actions.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!name.trim() || conditions.length === 0 || actions.length === 0) {
      return;
    }

    setSaving(true);
    try {
      await onSave({
        name,
        enabled,
        priority,
        runOn,
        conditions: {
          type: conditionType,
          conditions,
        },
        actions,
      });
    } catch (error) {
      console.error('Failed to save rule', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between p-4 border-b border-border/40">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={onCancel} className="-ml-2">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-lg font-semibold">{rule ? 'Edit Rule' : 'Create Rule'}</h2>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        <div className="max-w-3xl mx-auto space-y-8">
          {/* Basic Settings */}
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="rule-name">Rule Name</Label>
                <Input
                  id="rule-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Move newsletters to folder"
                  className="max-w-lg"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="rule-run-on">Run On</Label>
                <Select value={runOn} onValueChange={(value) => setRunOn(value as 'inbound' | 'outbound' | 'both')}>
                  <SelectTrigger id="rule-run-on">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inbound">Inbound (Incoming emails)</SelectItem>
                    <SelectItem value="outbound">Outbound (Sent emails)</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="rule-priority">Priority (Order)</Label>
                <Input
                  id="rule-priority"
                  type="number"
                  value={priority}
                  onChange={(e) => setPriority(parseInt(e.target.value) || 100)}
                  min={0}
                  max={1000}
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2 pt-2">
              <Switch id="rule-enabled" checked={enabled} onCheckedChange={setEnabled} />
              <Label htmlFor="rule-enabled" className="font-normal text-muted-foreground">Enable this rule</Label>
            </div>
          </div>

          {/* Conditions */}
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b border-border/40 pb-2">
              <div className="space-y-1">
                <h3 className="text-base font-medium">Conditions</h3>
                <p className="text-xs text-muted-foreground">When should this rule apply?</p>
              </div>
              <Button size="sm" variant="outline" onClick={addCondition} className="gap-2">
                <Plus className="h-3.5 w-3.5" />
                Add Condition
              </Button>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                <span>Match</span>
                <Select value={conditionType} onValueChange={(value) => setConditionType(value as RuleConditions['type'])}>
                  <SelectTrigger className="w-24 h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="any">Any</SelectItem>
                  </SelectContent>
                </Select>
                <span>of the following conditions:</span>
              </div>

              {conditions.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border/60 p-8 text-center text-sm text-muted-foreground bg-muted/20">
                  No conditions added. Click "Add Condition" to start.
                </div>
              ) : (
                <div className="space-y-2">
                  {conditions.map((condition, idx) => (
                    <ConditionEditor
                      key={idx}
                      condition={condition}
                      onChange={(updates) => updateCondition(idx, updates)}
                      onRemove={() => removeCondition(idx)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b border-border/40 pb-2">
              <div className="space-y-1">
                <h3 className="text-base font-medium">Actions</h3>
                <p className="text-xs text-muted-foreground">What should happen when conditions are met?</p>
              </div>
              <Button size="sm" variant="outline" onClick={addAction} className="gap-2">
                <Plus className="h-3.5 w-3.5" />
                Add Action
              </Button>
            </div>

            <div className="space-y-3">
              {actions.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border/60 p-8 text-center text-sm text-muted-foreground bg-muted/20">
                  No actions added. Click "Add Action" to define what this rule does.
                </div>
              ) : (
                <div className="space-y-2">
                  {actions.map((action, idx) => (
                    <ActionEditor
                      key={idx}
                      action={action}
                      folders={folders}
                      onChange={(newAction) => updateAction(idx, newAction)}
                      onRemove={() => removeAction(idx)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-6 border-t border-border/40">
            <Button variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving || !name.trim() || conditions.length === 0 || actions.length === 0} className="gap-2">
              {saving ? 'Saving...' : 'Save Rule'}
              {!saving && <Save className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConditionEditor({
  condition,
  onChange,
  onRemove,
}: {
  condition: RuleCondition;
  onChange: (updates: Partial<RuleCondition>) => void;
  onRemove: () => void;
}) {
  const isBoolean = condition.field === 'hasAttachment';
  const isContactState = condition.field === 'contactApprovalState';
  const isSpamScore = condition.field === 'spamScore';
  const requiresValue = !isBoolean && !isContactState;

  const textOperators: Array<{ value: RuleConditionOperator; label: string }> = [
    { value: 'contains', label: 'Contains' },
    { value: 'equals', label: 'Equals' },
    { value: 'matches', label: 'Matches (regex)' },
  ];
  const numericOperators: Array<{ value: RuleConditionOperator; label: string }> = [
    { value: 'greaterThan', label: '>' },
    { value: 'greaterThanOrEqual', label: '>=' },
    { value: 'lessThan', label: '<' },
    { value: 'lessThanOrEqual', label: '<=' },
    { value: 'equals', label: '=' },
  ];
  const operatorOptions = isSpamScore ? numericOperators : textOperators;

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border border-border/60 bg-background/40 p-3 transition-colors hover:bg-muted/30">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <span className="text-sm font-medium text-muted-foreground w-12 shrink-0">If</span>
        <Select
          value={condition.field}
          onValueChange={(value) => {
            const nextField = value as RuleConditionField;
            if (nextField === 'spamScore') {
              onChange({
                field: nextField,
                operator: condition.operator && operatorOptions.some(opt => opt.value === condition.operator)
                  ? condition.operator
                  : 'greaterThanOrEqual',
                value: typeof condition.value === 'number' ? condition.value : 5,
              });
            } else if (condition.field === 'spamScore') {
              onChange({
                field: nextField,
                operator: 'contains',
                value: '',
              });
            } else {
              onChange({ field: nextField });
            }
          }}
        >
          <SelectTrigger className="w-[140px] h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="from">From</SelectItem>
            <SelectItem value="to">To</SelectItem>
            <SelectItem value="subject">Subject</SelectItem>
            <SelectItem value="body">Body</SelectItem>
            <SelectItem value="senderDomain">Sender domain</SelectItem>
            <SelectItem value="hasAttachment">Has Attachment</SelectItem>
            <SelectItem value="contactApprovalState">Contact State</SelectItem>
            <SelectItem value="spamScore">Spam Score</SelectItem>
          </SelectContent>
        </Select>

        {!isBoolean && !isContactState && (
          <Select
            value={condition.operator}
            onValueChange={(value) => onChange({ operator: value as RuleConditionOperator })}
          >
            <SelectTrigger className="w-[110px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {operatorOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {requiresValue && !isSpamScore && (
          <Input
            className="flex-1 min-w-[120px] h-9"
            value={String(condition.value)}
            onChange={(e) => onChange({ value: e.target.value })}
            placeholder="Value to match..."
          />
        )}

        {isSpamScore && (
          <Input
            className="w-24 h-9"
            type="number"
            step="0.1"
            value={typeof condition.value === 'number' ? condition.value : Number(condition.value) || 0}
            onChange={(e) => {
              const parsed = Number(e.target.value);
              onChange({ value: Number.isFinite(parsed) ? parsed : 0 });
            }}
            placeholder="Score"
          />
        )}

        {isBoolean && (
          <Select value={String(condition.value)} onValueChange={(v) => onChange({ value: v === 'true' })}>
            <SelectTrigger className="flex-1 min-w-[120px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="true">True</SelectItem>
              <SelectItem value="false">False</SelectItem>
            </SelectContent>
          </Select>
        )}

        {isContactState && (
          <Select value={String(condition.value)} onValueChange={(v) => onChange({ value: v })}>
            <SelectTrigger className="flex-1 min-w-[120px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="blocked">Blocked</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      <Button size="icon" variant="ghost" onClick={onRemove} className="h-8 w-8 text-muted-foreground hover:text-destructive shrink-0">
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

function ActionEditor({
  action,
  folders,
  onChange,
  onRemove,
}: {
  action: RuleAction;
  folders: Folder[];
  onChange: (action: RuleAction) => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border border-border/60 bg-background/40 p-3 transition-colors hover:bg-muted/30">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <span className="text-sm font-medium text-muted-foreground w-12 shrink-0">Then</span>
        <Select
          value={action.type}
          onValueChange={(typeValue) => {
            const nextType = typeValue as RuleAction['type'];
            if (nextType === 'moveToFolder') {
              onChange({ type: nextType, value: folders[0]?.path || 'INBOX' });
            } else if (nextType === 'markAsRead') {
              onChange({ type: nextType, value: true });
            } else {
              onChange({ type: nextType });
            }
          }}
        >
          <SelectTrigger className="w-[180px] h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="moveToFolder">Move to folder</SelectItem>
            <SelectItem value="markAsRead">Mark as read/unread</SelectItem>
            <SelectItem value="delete">Delete</SelectItem>
            <SelectItem value="approveContact">Approve contact</SelectItem>
            <SelectItem value="blockContact">Block contact</SelectItem>
            <SelectItem value="trainAsSpam">Train as spam</SelectItem>
            <SelectItem value="trainAsGood">Train as good</SelectItem>
          </SelectContent>
        </Select>

        {action.type === 'moveToFolder' && (
          <Select
            value={action.value}
            onValueChange={(v) => onChange({ ...action, value: v })}
          >
            <SelectTrigger className="flex-1 min-w-[120px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {folders.map((folder) => (
                <SelectItem key={folder.id} value={folder.path}>
                  {folder.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {action.type === 'markAsRead' && (
          <Select
            value={String(action.value)}
            onValueChange={(v) => onChange({ ...action, value: v === 'true' })}
          >
            <SelectTrigger className="flex-1 min-w-[120px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="true">Read</SelectItem>
              <SelectItem value="false">Unread</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      <Button size="icon" variant="ghost" onClick={onRemove} className="h-8 w-8 text-muted-foreground hover:text-destructive shrink-0">
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}
